import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { MetaOptionList, SwatchPicker } from '@/components/ai-tools/option-pickers';
import { EMPTY_PHOTO_SLOT, PhotoPicker, type PhotoSlot } from '@/components/ai-tools/photo-picker';
import { RenderGrid, type RenderGridItem } from '@/components/ai-tools/render-grid';
import { StickyActionBar } from '@/components/ai-tools/sticky-action-bar';
import { StepTitle } from '@/components/ai-tools/step-title';
import { TopicInput } from '@/components/ai-tools/topic-input';
import { Button } from '@/components/ui/button';
import { localized, type SinglePhotoTool } from '@/config/ai-tools';
import { useAuthGate } from '@/hooks/use-auth-gate';
import { useInView } from '@/hooks/use-in-view';
import { useRenderOutcomeTracking } from '@/hooks/use-render-outcome-tracking';
import { useStudioResume } from '@/hooks/use-studio-resume';
import { useAddStartedTask, useToolTasks } from '@/hooks/use-tool-tasks';
import { formatUsd } from '@/lib/ai-tools/format';
import { ENTRY_PACK, uploadToolPhoto } from '@/lib/ai-tools/studio';
import type { PublicToolTask } from '@/lib/ai-tools/types';
import { ApiError, apiPost } from '@/lib/api-client';
import { track } from '@/lib/track';
import type { SlotEntry } from '@/lib/store/recommendation-slot';
import type { ToolSummary } from '@/modules/directory/types';
import { m } from '@/paraglide/messages.js';
import { getLocale } from '@/paraglide/runtime.js';
import { RecommendationStrip } from '@/blocks/store/recommendation-slot';
import { resolvePhotoToolCopy } from './photo-tool-copy';
import { StudioPacks } from './studio-packs';

/** The form as it is parked across the Stripe round trip. */
type StudioDraft = { photoUrl: string; styleId: string; qualityId: string; note: string };

const errorCopy: Record<string, () => string> = {
  generator_unavailable: () => m['directory.ai_tools.error.generator_unavailable'](),
  too_many_renders: () => m['directory.ai_tools.photo.too_many'](),
  invalid_photo: () => m['directory.ai_tools.photo.invalid_photo'](),
};

/** Wording that depends on whether the engine returns an image or a video. */
function mediaCopy(video: boolean) {
  return video
    ? {
        label: () => m['directory.ai_tools.studio.label'](),
        started: () => m['directory.ai_tools.studio.started'](),
        tooMany: () => m['directory.ai_tools.error.too_many_renders'](),
        rendersTitle: () => m['directory.ai_tools.renders.title'](),
        rendering: () => m['directory.ai_tools.renders.rendering'](),
        failed: () => m['directory.ai_tools.renders.failed'](),
        download: () => m['directory.ai_tools.renders.download'](),
      }
    : {
        label: () => m['directory.ai_tools.photo.label'](),
        started: () => m['directory.ai_tools.photo.started'](),
        tooMany: () => m['directory.ai_tools.photo.too_many'](),
        rendersTitle: () => m['directory.ai_tools.photo.renders_title'](),
        rendering: () => m['directory.ai_tools.photo.rendering'](),
        failed: () => m['directory.ai_tools.photo.failed'](),
        download: () => m['directory.ai_tools.photo.download'](),
      };
}

/**
 * The one-photo generator panel: one photo, a style, a quality tier and an
 * optional note → one credit-charged image (photo-to-photo) or short video
 * (photo-video). Owns state and API calls; every piece of UI is a props-only
 * component (same layout as the duo studio).
 */
export function PhotoStyleStudio({
  tool,
  recommendations = [],
  onNeedCredits,
}: {
  tool: SinglePhotoTool;
  /** Trend-page slot entries; shown under the results once one finishes. */
  recommendations?: SlotEntry<ToolSummary>[];
  onNeedCredits: () => void;
}) {
  const locale = getLocale();
  const t = (value: Parameters<typeof localized>[0]) => localized(value, locale);
  const copy = resolvePhotoToolCopy(tool, locale);
  const video = tool.kind === 'photo-video';
  const media = mediaCopy(video);
  const { isAuthenticated, openGate, gate } = useAuthGate('generate');
  const [photo, setPhoto] = useState<PhotoSlot>(EMPTY_PHOTO_SLOT);
  const [styleId, setStyleId] = useState(tool.styles[0]?.id ?? '');
  const [qualityId, setQualityId] = useState(tool.qualities[0]?.id ?? '');
  const [note, setNote] = useState('');
  // Set when Generate was pressed signed out: runs once the sign-in dialog succeeds.
  const runAfterSignIn = useRef(false);

  const quality = tool.qualities.find((item) => item.id === qualityId) ?? tool.qualities[0];
  const tasksQuery = useToolTasks(tool.slug, isAuthenticated);
  const addStartedTask = useAddStartedTask(tool.slug);
  const balance = tasksQuery.data?.balance;
  const [studioRef, studioInView] = useInView<HTMLElement>();
  const [actionRef, actionInView] = useInView<HTMLDivElement>();
  const perRender = ENTRY_PACK
    ? formatUsd(Math.round((quality.credits * ENTRY_PACK.priceInCents) / ENTRY_PACK.credits))
    : null;
  const lowOnCredits = balance !== undefined && balance < quality.credits;
  const standardCredits = Math.min(...tool.qualities.map((item) => item.credits));

  const pickPhoto = async (file: File) => {
    const preview = URL.createObjectURL(file);
    // Signed out: keep the file in memory; it uploads once the visitor signs in.
    if (!isAuthenticated) {
      setPhoto({ status: 'local', preview, file });
      return;
    }
    setPhoto({ status: 'uploading', preview });
    try {
      setPhoto({ status: 'ready', preview, url: await uploadToolPhoto(file) });
    } catch {
      setPhoto({ status: 'error', preview });
    }
  };

  /** Uploads a photo picked before sign-in; resolves to the settled slot. */
  const settlePhoto = async (): Promise<PhotoSlot> => {
    if (photo.status !== 'local' || !photo.file) return photo;
    setPhoto({ status: 'uploading', preview: photo.preview });
    try {
      const ready: PhotoSlot = { status: 'ready', preview: photo.preview, url: await uploadToolPhoto(photo.file) };
      setPhoto(ready);
      return ready;
    } catch {
      const failed: PhotoSlot = { status: 'error', preview: photo.preview };
      setPhoto(failed);
      return failed;
    }
  };

  const generate = useMutation({
    mutationFn: async () => {
      const settled = await settlePhoto();
      if (settled.status !== 'ready' || !settled.url) throw new Error('upload_failed');
      return apiPost<PublicToolTask>(`/api/ai-tools/${tool.slug}/generate`, {
        style: styleId,
        quality: quality.id,
        photo: settled.url,
        note,
        locale,
      });
    },
    onSuccess: (task) => {
      toast.success(media.started());
      addStartedTask(task);
      track('tool_generate_started', { tool: tool.slug, quality: quality.id, credits: quality.credits });
    },
    onError: (error: Error) => {
      const code = error instanceof ApiError ? error.message : '';
      // The balance on screen was stale: refresh it so the packs appear.
      if (code === 'insufficient_credits') return void tasksQuery.refetch();
      if (code === 'too_many_renders') return void toast.error(media.tooMany());
      toast.error((errorCopy[code] ?? (() => m['directory.ai_tools.error.generic']()))());
    },
  });

  /** Signed in with a photo: check the balance first, so a short balance goes straight to the packs. */
  const run = async () => {
    const available = balance ?? (await tasksQuery.refetch()).data?.balance;
    if (available !== undefined && available < quality.credits) {
      track('tool_generate_short', { tool: tool.slug, missing: quality.credits - available });
      return;
    }
    generate.mutate();
  };

  const onGenerate = () => {
    track('tool_generate_click', { tool: tool.slug, authed: isAuthenticated, valid: photo.status === 'local' || photo.status === 'ready' });
    if (photo.status === 'empty') return void toast.error(m['directory.ai_tools.photo.need_photo']());
    if (photo.status === 'error') return void toast.error(m['directory.ai_tools.studio.photo_failed']());
    if (photo.status === 'uploading') return;
    if (!isAuthenticated) {
      runAfterSignIn.current = true;
      track('tool_signin_gate', { tool: tool.slug });
      openGate();
      return;
    }
    void run();
  };

  useEffect(() => {
    if (isAuthenticated && runAfterSignIn.current) {
      runAfterSignIn.current = false;
      void run();
    }
  }, [isAuthenticated]);

  const getDraft = async (): Promise<StudioDraft | null> => {
    const settled = await settlePhoto();
    if (settled.status !== 'ready' || !settled.url) return null;
    return { photoUrl: settled.url, styleId, qualityId: quality.id, note };
  };

  const { resuming } = useStudioResume<StudioDraft>({
    slug: tool.slug,
    isAuthenticated,
    restore: (draft) => {
      setPhoto({ status: 'ready', preview: draft.photoUrl, url: draft.photoUrl });
      setStyleId(draft.styleId);
      setQualityId(draft.qualityId);
      setNote(draft.note);
    },
    refetchBalance: async () => (await tasksQuery.refetch()).data?.balance,
    needed: quality.credits,
    start: () => generate.mutate(),
  });

  const renders: RenderGridItem[] = (tasksQuery.data?.tasks ?? []).map((task) => {
    const style = tool.styles.find((item) => item.id === task.stage);
    return {
      id: task.id,
      state: task.status === 'success' ? 'ready' : task.status === 'failed' ? 'failed' : 'rendering',
      videoUrl: video ? task.videoUrl : null,
      imageUrl: video ? null : task.imageUrl,
      swatch: style?.swatch,
      caption: [style ? t(style.label) : '', task.topic].filter(Boolean).join(' · '),
    };
  });
  useRenderOutcomeTracking(tool.slug, renders);
  // Plan §4.5: the slot appears only after a render succeeds or fails.
  const hasFinishedRender = renders.some((render) => render.state !== 'rendering');

  return (
    <section
      ref={studioRef}
      id="studio"
      aria-label={media.label()}
      className="scroll-mt-24 rounded-2xl border border-border bg-card p-5 shadow-xl shadow-black/5 sm:p-8 lg:p-10"
    >
      {gate}
      <div className="grid gap-10 lg:grid-cols-2 lg:gap-12">
        <div className="space-y-8">
          <div>
            <StepTitle index={1}>{m['directory.ai_tools.photo.step_photo']()}</StepTitle>
            <div className="max-w-[12rem]">
              <PhotoPicker
                label={copy.photoLabel}
                labels={{
                  add: m['directory.ai_tools.studio.photo_add'](),
                  uploading: m['directory.ai_tools.studio.photo_uploading'](),
                  remove: m['directory.ai_tools.studio.photo_remove'](),
                  failed: m['directory.ai_tools.studio.photo_failed'](),
                }}
                slot={photo}
                blocked={false}
                onBlocked={() => undefined}
                onPick={(file) => void pickPhoto(file)}
                onClear={() => setPhoto(EMPTY_PHOTO_SLOT)}
              />
            </div>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">{copy.photoHint}</p>
          </div>

          <div>
            <StepTitle index={2}>{copy.stepStyle}</StepTitle>
            <SwatchPicker
              options={tool.styles.map((style) => ({
                id: style.id,
                label: t(style.label),
                swatch: style.swatch,
                image: style.image,
                video: style.video,
              }))}
              value={styleId}
              onChange={setStyleId}
            />
          </div>
        </div>

        <div className="space-y-8">
          <div>
            <StepTitle index={3}>{m['directory.ai_tools.studio.step_quality']()}</StepTitle>
            <MetaOptionList
              options={tool.qualities.map((item) => ({
                id: item.id,
                label: t(item.label),
                meta: m['directory.ai_tools.studio.credits']({ credits: item.credits }),
              }))}
              value={quality.id}
              onChange={setQualityId}
            />
          </div>

          {tool.noteMaxLength > 0 ? (
            <div>
              <StepTitle
                index={4}
                aside={<span className="text-xs text-muted-foreground">{m['directory.ai_tools.studio.optional']()}</span>}
              >
                {copy.stepNote}
              </StepTitle>
              <TopicInput
                presets={[]}
                value={note}
                onChange={setNote}
                maxLength={tool.noteMaxLength}
                placeholder={copy.notePlaceholder}
                hint={copy.noteHint}
              />
            </div>
          ) : null}

          <div ref={actionRef} className="space-y-3">
            {perRender ? (
              <div>
                <p className="text-2xl font-bold tracking-tight">
                  {m['directory.ai_tools.studio.price_headline']({ price: perRender })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {m['directory.ai_tools.studio.price_sub']({ credits: quality.credits })}
                </p>
              </div>
            ) : null}
            <Button
              size="lg"
              className="h-12 w-full text-base"
              disabled={generate.isPending || resuming}
              onClick={onGenerate}
            >
              {generate.isPending || resuming ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  {resuming ? m['directory.ai_tools.studio.resuming']() : m['directory.ai_tools.photo.starting']()}
                </>
              ) : (
                <>
                  <Sparkles className="size-4" aria-hidden="true" />
                  {copy.generate(quality.credits)}
                </>
              )}
            </Button>
            {!isAuthenticated ? (
              <p className="text-xs text-muted-foreground">{m['directory.ai_tools.studio.signin_hint']()}</p>
            ) : null}
            {lowOnCredits && !resuming ? (
              <StudioPacks
                slug={tool.slug}
                missing={quality.credits - (balance ?? 0)}
                standardCredits={standardCredits}
                unit={video ? 'video' : 'image'}
                getDraft={getDraft}
              />
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              {balance !== undefined ? (
                <button type="button" onClick={onNeedCredits} className="font-medium text-primary hover:underline">
                  {m['directory.ai_tools.studio.balance']({ credits: balance })} · {m['directory.ai_tools.studio.top_up']()}
                </button>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{m['directory.ai_tools.photo.consent']()}</p>
          </div>
        </div>
      </div>

      {renders.length > 0 ? (
        <div className="mt-10 space-y-8">
          <RenderGrid
            title={media.rendersTitle()}
            items={renders}
            onDownload={() => track('tool_download', { tool: tool.slug })}
            aspect={video ? 'tall' : 'portrait'}
            labels={{
              rendering: media.rendering(),
              failed: media.failed(),
              download: media.download(),
              waitHint: m['directory.ai_tools.renders.wait_hint'](),
            }}
          />
          {hasFinishedRender ? <RecommendationStrip trendSlug={tool.slug} entries={recommendations} /> : null}
        </div>
      ) : null}

      <StickyActionBar
        visible={studioInView && !actionInView}
        meta={m['directory.ai_tools.studio.sticky_meta']({ credits: quality.credits })}
        label={m['directory.ai_tools.studio.generate_short']()}
        busy={generate.isPending}
        onAction={onGenerate}
      />
    </section>
  );
}
